import React, { createContext, useState, useMemo, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { useSellerOnboardingJson } from '../../../hooks/use-seller-setting';
import { useVariantPages } from '../hooks/use-variant-pages';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';
import { ONBOARDING, ONBOARDING_SETUP_EVENTS } from '../constants';
import messages from '../messages';
import { ONBOARD_PATH } from '../../../routes/constants';
import { saveNewSellerSettings } from '../../../store/seller/seller.actions';
import { updateSellerSettingsBulk } from '../../settings/api/save-settings';
import { useProgressiveDisclosureSegmentTracking } from '../hooks/use-progressive-disclosure-segment-tracking';

export const SetupContext = createContext({});
export const useSetupContext = () => useContext(SetupContext);

export const SetupContextProvider = ({ children }) => {
  const sellerOnboardingJson = useSellerOnboardingJson();
  const variantBasedPageOrder = useVariantPages();
  const history = useHistory();
  const { search } = useLocation();
  const shouldUseTransitionEffect = search.includes('firsttime=true');
  const initialPage = sellerOnboardingJson.currentPage || variantBasedPageOrder[0];
  const [currentSetupStep, setCurrentSetupStep] = useState(initialPage);
  const [isSetupContextLoaded, setIsSetupContextLoaded] = useState(!shouldUseTransitionEffect);
  const dispatch = useDispatch();
  const trackSetupSaveError = useProgressiveDisclosureSegmentTracking(ONBOARDING_SETUP_EVENTS.PROGRESSIVE_DISCLOSURE_SAVE_JSON_ERROR);
  const trackCompletedOnboardingSetup = useProgressiveDisclosureSegmentTracking(
    ONBOARDING_SETUP_EVENTS.PROGRESSIVE_DISCLOSURE_SETUP_COMPLETE,
  );

  useOnComponentMount(() => {
    setTimeout(() => {
      setIsSetupContextLoaded(true);
    }, 3000);
  });

  const saveSellerOnboardingJson = useCallback(
    async (pageToNavigateTo, data) => {
      const updatedJson = {
        ...sellerOnboardingJson,
        currentPage: pageToNavigateTo,
      };
      if (data) {
        updatedJson[currentSetupStep] = data;
      }

      const settingsToSave = {
        [SellerSettings.sellerOnboardingJson.key]: JSON.stringify(updatedJson),
      };
      const isSetupComplete = pageToNavigateTo === ONBOARDING;
      try {
        const newSettings = await updateSellerSettingsBulk(settingsToSave);
        dispatch(saveNewSellerSettings(newSettings));
        if (isSetupComplete) {
          trackCompletedOnboardingSetup({ page: ONBOARDING });
        }
        return true;
      } catch {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.saveSellerOnboardingJsonFailure} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        trackSetupSaveError({ page: currentSetupStep });
      }
      return false;
    },
    [currentSetupStep, dispatch, sellerOnboardingJson, trackCompletedOnboardingSetup, trackSetupSaveError],
  );

  const currentPageIndex = variantBasedPageOrder.indexOf(currentSetupStep);
  const currentStepNumber = currentPageIndex + 1;
  const totalNumberOfSteps = variantBasedPageOrder.length;

  const getVariantBasedPaging = useCallback(() => {
    const onFinalPage = currentPageIndex === variantBasedPageOrder.length - 1;
    const nextPage = onFinalPage ? ONBOARDING : variantBasedPageOrder[currentPageIndex + 1];
    const prevPage = currentPageIndex === 0 ? currentSetupStep : variantBasedPageOrder[currentPageIndex - 1];
    return { nextPage, prevPage };
  }, [variantBasedPageOrder, currentSetupStep, currentPageIndex]);

  const onBack = useCallback(
    async (data) => {
      const { prevPage } = getVariantBasedPaging();
      const didSaveSellerJson = await saveSellerOnboardingJson(prevPage, data);
      if (!didSaveSellerJson) {
        return;
      }
      setCurrentSetupStep(prevPage);
    },
    [getVariantBasedPaging, saveSellerOnboardingJson],
  );

  const onContinue = useCallback(
    async (data) => {
      const { nextPage } = getVariantBasedPaging();
      const didSaveSellerJson = await saveSellerOnboardingJson(nextPage, data);
      if (!didSaveSellerJson) {
        return;
      }
      if (nextPage === ONBOARDING) {
        history.push(ONBOARD_PATH);
      } else {
        setCurrentSetupStep(nextPage);
      }
    },
    [getVariantBasedPaging, history, saveSellerOnboardingJson],
  );

  const value = useMemo(
    () => ({
      currentSetupStep,
      onContinue,
      onBack,
      setCurrentSetupStep,
      shouldUseTransitionEffect,
      isSetupContextLoaded,
      currentStepNumber,
      totalNumberOfSteps,
    }),
    [
      currentSetupStep,
      onContinue,
      onBack,
      setCurrentSetupStep,
      shouldUseTransitionEffect,
      isSetupContextLoaded,
      currentStepNumber,
      totalNumberOfSteps,
    ],
  );

  return <SetupContext.Provider value={value}>{children}</SetupContext.Provider>;
};

SetupContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default {
  SetupContext,
  SetupContextProvider,
};
